import { DatePickerType } from './types';
import { INTERNAL_DATE_FORMAT } from '../../../config/constants';
import moment, { Moment } from 'moment';
import { DisplayDateFormat, globalFormat } from '../../../utils/formatting';
import { freeze } from '@reduxjs/toolkit';
import {
  assertCondition,
  GraphQLLocalDate,
  GraphQLUnionMember,
} from '../../../utils/types';
import {
  IAnalysisInfoFieldsFragment,
  IDatePickerFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import { IDateInput } from '../../../redux/slices/oldBuilder/date/types';
import { z } from 'zod';
import { isNil } from 'lodash';

export const inputDateFormatDictionary: Record<DisplayDateFormat, string> =
  freeze({
    'YYYY-MM-DD': 'YYYY-M-D',
    'MM/DD/YYYY': 'M/D/YYYY',
    'DD/MM/YYYY': 'D/M/YYYY',
    'DD.MM.YYYY': 'D.M.YYYY',
  });

const dateSchema = z.string().regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/);

export const localDateToMoment = (date: GraphQLLocalDate) =>
  moment(dateSchema.parse(date), INTERNAL_DATE_FORMAT, true);

/**
 * ALL YYYY-MM-DD for store and submit to backend
 * Using Configured date format to display and input.
 */
export const convertInternalDateFormatToDisplayDateFormat = (
  date: GraphQLLocalDate,
) => {
  const { dateFormat } = globalFormat.getGlobalFormat();
  return localDateToMoment(date).format(inputDateFormatDictionary[dateFormat]);
};

export const convertDisplayDateFormatToInternalDateFormat = (
  date: string | Date,
) => {
  const { dateFormat } = globalFormat.getGlobalFormat();
  return moment(date, inputDateFormatDictionary[dateFormat], true).format(
    INTERNAL_DATE_FORMAT,
  );
};

export const toJavaScriptDate = (date: GraphQLLocalDate): Date =>
  localDateToMoment(date).toDate();

// get the difference in days based on a start date and end date
/**
 * check if start date is after end date or end date is before start date
 * if so, set the input to the correct input.
 * if input start > end, set end to start
 * if input end < start, set start to end
 *
 * @param start updated start date
 * @param end updated end date
 * @param inputType clicking on start date picker or end date picker
 * @returns revised start and end date. always in correct input
 */
export const toCanonicalDateRange = (
  start: string,
  end: string,
  inputType: 'start' | 'end',
) => {
  if (inputType === 'start' && moment(start).isAfter(moment(end))) {
    end = start;
  }
  if (inputType === 'end' && moment(end).isBefore(moment(start))) {
    start = end;
  }
  return {
    revisedStart: start,
    revisedEnd: end,
  };
};

export const DatePickerTypeLabel = freeze<Record<DatePickerType, string>>({
  DatePickerLast: 'Last',
  DatePickerSince: 'Since',
  DatePickerFixed: 'Fixed',
  DatePickerLastFull: 'Last Full',
  DatePickerStartOf: 'Start Of',
});

const toStartEndDates = (
  startDateMoment: Moment,
  endDateMoment: Moment,
): Pick<IDateInput, 'startDate' | 'endDate'> => {
  assertCondition(
    startDateMoment.isBefore(endDateMoment, 'date') ||
      startDateMoment.isSame(endDateMoment, 'date'),
  );
  return {
    startDate: startDateMoment.format(INTERNAL_DATE_FORMAT),
    endDate: endDateMoment.format(INTERNAL_DATE_FORMAT),
  };
};

export const getYesterdayDatePicker = (
  endDate: GraphQLLocalDate,
): GraphQLUnionMember<IDatePickerFieldsFragment, 'DatePickerFixed'> => {
  const endDateMoment = localDateToMoment(endDate);
  const startDateMoment = endDateMoment.clone().subtract(1, 'day');
  const { startDate: start, endDate: end } = toStartEndDates(
    startDateMoment,
    endDateMoment,
  );
  return freeze<
    GraphQLUnionMember<IDatePickerFieldsFragment, 'DatePickerFixed'>
  >({
    __typename: 'DatePickerFixed',
    start,
    end,
  });
};

export const getTodayDatePicker = (
  endDate: GraphQLLocalDate,
): GraphQLUnionMember<IDatePickerFieldsFragment, 'DatePickerFixed'> => {
  const endDateMoment = localDateToMoment(endDate);
  const { startDate: start, endDate: end } = toStartEndDates(
    endDateMoment,
    endDateMoment,
  );
  return freeze<
    GraphQLUnionMember<IDatePickerFieldsFragment, 'DatePickerFixed'>
  >({
    __typename: 'DatePickerFixed',
    start,
    end,
  });
};

export const datePickerFromAnalysisInfo = (
  analysisInfo: Pick<
    IAnalysisInfoFieldsFragment,
    'datePicker' | 'start' | 'end'
  >,
): IDatePickerFieldsFragment => {
  const { start, end, datePicker } = analysisInfo;
  if (!isNil(datePicker)) {
    return datePicker;
  } else {
    // legacy analyses which don't have relative-time-period date picker
    // use the start/end date and construct it as fixed
    return freeze<
      GraphQLUnionMember<IDatePickerFieldsFragment, 'DatePickerFixed'>
    >({
      __typename: 'DatePickerFixed',
      start,
      end,
    });
  }
};

export const datePickerFromDateInput = (
  dateInput: IDateInput,
): IDatePickerFieldsFragment =>
  datePickerFromAnalysisInfo({
    datePicker: dateInput.datePicker ?? null,
    start: dateInput.startDate,
    end: dateInput.endDate,
  });
